import React from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import { IconButton, Tooltip, Typography } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItemCheckbox, TreeItemContent, TreeItemProvider, TreeItemRoot, useTreeItem } from '@mui/x-tree-view';

// MUI Icons
import LocationOnIcon from '@mui/icons-material/LocationOn';

// Custom components
import MapCard from '@/ui/MapCard/MapCard';
import MapCardHeader from '@/ui/MapCard/MapCardHeader';
import MapCardAvatar from '@/ui/MapCard/MapCardAvatar';
import MapCardContent from '@/ui/MapCard/MapCardContent';
import MapCardHeaderButtonClose from '@/ui/MapCard/MapCardHeaderButtonClose';

export type CesiumModel = {
  index: number;
  name: string;
  visible: boolean;
  loaded: boolean;
};

type CesiumModelTreeItemProps = {
  model: CesiumModel;
  onToggleVisible: (index: number) => void;
  onZoomTo: (index: number) => void;
};

const CesiumModelTreeItem = (props: CesiumModelTreeItemProps) => {
  const { model, onToggleVisible, onZoomTo } = props;

  const { t } = useTranslation();

  const id = `model-${model.index}`;

  const { getRootProps, getContentProps, getCheckboxProps, getLabelProps } = useTreeItem({ id, itemId: id, label: model.name });

  return (
    // @ts-ignore same typing issue as in LayerTreeItem
    <TreeItemProvider itemId={id}>
      <TreeItemRoot {...getRootProps()}>
        <TreeItemContent
          {...getContentProps()}
          sx={{ gap: 0, p: 0, cursor: model.loaded ? 'pointer' : 'default' }}
          onClick={() => {
            if (model.loaded) onToggleVisible(model.index);
          }}
        >
          <TreeItemCheckbox
            {...getCheckboxProps()}
            sx={{ padding: '9px' }}
            color="primary"
            checked={model.visible}
            disabled={!model.loaded}
          />
          <Typography component="div" sx={{ width: '100%' }} {...getLabelProps()}>
            {model.name}
          </Typography>
          <Tooltip title={t('3D:models.zoom_to') as string} placement="right">
            <span>
              <IconButton
                color="primary"
                size="small"
                disabled={!model.loaded}
                onClick={(evt) => {
                  evt.stopPropagation();
                  onZoomTo(model.index);
                }}
              >
                <LocationOnIcon />
              </IconButton>
            </span>
          </Tooltip>
        </TreeItemContent>
      </TreeItemRoot>
    </TreeItemProvider>
  );
};

type CesiumModelTreeProps = {
  models: CesiumModel[];
  onToggleVisible: (index: number) => void;
  onZoomTo: (index: number) => void;
  onClose: () => void;
};

const CesiumModelTree = (props: CesiumModelTreeProps) => {
  const { models, onToggleVisible, onZoomTo, onClose } = props;

  const { t } = useTranslation();

  return (
    <MapCard sx={{ width: 300 }}>
      <MapCardHeader
        title={t('3D:models.title')}
        avatar={<MapCardAvatar ariaLabel="models" icon="layers" />}
        action={<MapCardHeaderButtonClose onClick={onClose} />}
      />
      <MapCardContent style={{ maxHeight: '50vh', overflowY: 'auto' }}>
        {/* selection is driven by our own row click, same trick as LayerTree */}
        <SimpleTreeView multiSelect checkboxSelection selectedItems={[]}>
          {models.map((model) => (
            <CesiumModelTreeItem key={model.index} model={model} onToggleVisible={onToggleVisible} onZoomTo={onZoomTo} />
          ))}
        </SimpleTreeView>
      </MapCardContent>
    </MapCard>
  );
};

export default CesiumModelTree;
